import React, { useRef, useState, useEffect } from "react";
import {
  IconBubbleText,
  IconPlayerPlay,
  IconPlayerPause,
  IconPencil,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import styles from "./MDocumentCard.module.css";
import MOptionsMenu from "./MOptionsMenu";
import { MButton } from "./MButtons";
import MTextArea from "./MTextArea";
import { useToast } from "./MToastProvider";

interface HealthMemoryCardProps {
  title: string;
  year?: number;
  month?: number;
  onClick?: () => void;
  optionsMenu?: Array<{
    id: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    className?: string;
  }>;
  // Voice-note player props (Phase 56)
  hasVoiceNote?: boolean;
  voiceNoteDuration?: number;
  playingId?: string | null;
  onPlay?: (id: string) => void;
  id?: string;
  onFetchVoiceNoteUrl?: () => Promise<string>;
  // Transcript props (Phase 58)
  transcript?: string;
  transcriptStatus?: "none" | "pending" | "processing" | "completed" | "failed";
  transcriptEdited?: boolean;
  onPatchTranscript?: (id: string, text: string) => Promise<void>;
  onRetryTranscript?: (id: string) => Promise<void>;
}

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const HealthMemoryCard: React.FC<HealthMemoryCardProps> = ({
  title,
  year,
  month,
  onClick,
  optionsMenu,
  hasVoiceNote,
  voiceNoteDuration,
  playingId,
  onPlay,
  id,
  onFetchVoiceNoteUrl,
  transcript,
  transcriptStatus,
  transcriptEdited,
  onPatchTranscript,
  onRetryTranscript,
}) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const signedUrlRef = useRef<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Transcript local state (Phase 58) — all card-local, no global state needed
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(transcript ?? "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const { showToast } = useToast();

  // Pause when another card starts playing (D-56-09)
  useEffect(() => {
    if (playingId !== id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [playingId, id, isPlaying]);

  // One-retry on audio error — Pitfall 5 (no HTTP status; retry unconditionally, capped at one)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onError = async () => {
      if (signedUrlRef.current && onFetchVoiceNoteUrl) {
        signedUrlRef.current = null;
        const url = await onFetchVoiceNoteUrl();
        signedUrlRef.current = url;
        audio.src = url;
        audio.load();
        audio.play();
        setIsPlaying(true);
      }
    };
    audio.addEventListener("error", onError);
    return () => audio.removeEventListener("error", onError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlayPause = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    if (!signedUrlRef.current && onFetchVoiceNoteUrl) {
      const url = await onFetchVoiceNoteUrl();
      signedUrlRef.current = url;
      audioRef.current.src = url;
      audioRef.current.load();
    }
    if (id) onPlay?.(id);
    audioRef.current.play().catch(() => {
      setIsPlaying(false);
    });
    setIsPlaying(true);
  };

  const getDateDisplay = (): string | null => {
    if (!year) return null;
    if (month) {
      const date = new Date(year, month - 1);
      const monthName = date.toLocaleString("default", { month: "long" });
      return t("healthMemory.card.dateWithMonthYear", {
        month: monthName,
        year,
      });
    }
    return t("healthMemory.card.dateWithYear", { year });
  };

  const dateDisplay = getDateDisplay();

  return (
    <div
      className={`${styles.card}${onClick ? ` ${styles.clickable}` : ""}`}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className={styles.cardContent}>
        {/* Document Title */}
        <div className={styles.title}>{title}</div>

        {/* Category and Date Row */}
        <div className={styles.categoryDateRow}>
          <div className={styles.categoryInfo}>
            <div className={styles.categoryIcon}>
              <IconBubbleText size={20} />
            </div>
            <span className={styles.categoryName}>
              {t("healthMemory.card.categoryLabel")}
            </span>
          </div>
          <div className={styles.dateInfo}>
            {dateDisplay ? (
              <span className={styles.dateText}>{dateDisplay}</span>
            ) : null}
          </div>
        </div>

        {/* Voice-note player row — D-56-07, D-56-08 */}
        {hasVoiceNote && (
          <div className={styles.playerRow}>
            <button
              className="p-0 bg-transparent border-0 cursor-pointer flex items-center"
              onClick={handlePlayPause}
              aria-label={
                isPlaying
                  ? t("healthMemory.voiceNote.pauseAriaLabel")
                  : t("healthMemory.voiceNote.playAriaLabel")
              }
            >
              {isPlaying ? (
                <IconPlayerPause
                  size={18}
                  color="var(--colour-text-primary, #45168C)"
                />
              ) : (
                <IconPlayerPlay
                  size={18}
                  color="var(--colour-text-primary, #45168C)"
                />
              )}
            </button>
            {voiceNoteDuration !== undefined && (
              <span className={styles.playerDuration}>
                {formatTime(voiceNoteDuration)}
              </span>
            )}
            <audio
              ref={audioRef}
              preload="none"
              onEnded={() => setIsPlaying(false)}
            />
          </div>
        )}

        {/* Transcript section — D-58-01, D-58-03: status-driven, expanded by default */}
        {transcriptStatus && transcriptStatus !== "none" && (
          <div className={styles.transcriptSection}>
            {/* Header row: label + optional (edited) badge + edit icon */}
            <div className={styles.transcriptHeader}>
              <span className={styles.categoryName}>
                {t("healthMemory.transcript.label")}
              </span>
              {transcriptEdited && (
                <span className={`${styles.categoryName} italic`}>
                  {t("healthMemory.transcript.editedBadge")}
                </span>
              )}
              {transcriptStatus === "completed" && !isEditing && (
                <button
                  className={`${styles.editIconButton} min-h-[44px] min-w-[44px] bg-transparent border-0 cursor-pointer flex items-center justify-center`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDraftText(transcript ?? "");
                    setIsEditing(true);
                  }}
                  aria-label={t("healthMemory.transcript.editAriaLabel")}
                >
                  <IconPencil
                    size={16}
                    color="var(--colour-text-primary, #45168C)"
                  />
                </button>
              )}
            </div>

            {/* Status-driven body */}
            {(transcriptStatus === "pending" ||
              transcriptStatus === "processing") && (
              <span
                role="status"
                aria-live="polite"
                className={`${styles.playerDuration} italic`}
              >
                {t("healthMemory.transcript.transcribing")}
              </span>
            )}

            {transcriptStatus === "completed" && !isEditing && (
              <>
                <p
                  className={`${styles.transcriptText} ${
                    !isExpanded ? "line-clamp-3" : ""
                  }`}
                >
                  {transcript}
                </p>
                <button
                  className="text-[12px] bg-transparent border-0 cursor-pointer p-0 text-left"
                  style={{ color: "var(--colour-text-primary, #45168C)" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((v) => !v);
                  }}
                >
                  {isExpanded
                    ? t("healthMemory.transcript.showLess")
                    : t("healthMemory.transcript.showMore")}
                </button>
              </>
            )}

            {transcriptStatus === "completed" && isEditing && (
              <>
                <MTextArea
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  rows={4}
                  aria-label={t(
                    "healthMemory.transcript.editTextareaAriaLabel"
                  )}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className={styles.transcriptEditActions}>
                  <MButton
                    variant="filled"
                    size="sm"
                    disabled={isSaving}
                    aria-busy={isSaving}
                    aria-disabled={isSaving}
                    loading={isSaving}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setIsSaving(true);
                      try {
                        await onPatchTranscript?.(id!, draftText);
                        setIsEditing(false);
                      } catch {
                        showToast({
                          message: t("healthMemory.transcript.saveFailToast"),
                          variant: "error",
                        });
                      } finally {
                        setIsSaving(false);
                      }
                    }}
                  >
                    {t("healthMemory.transcript.save")}
                  </MButton>
                  <MButton
                    variant="link"
                    size="sm"
                    disabled={isSaving}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(false);
                    }}
                  >
                    {t("healthMemory.transcript.cancelEdit")}
                  </MButton>
                </div>
              </>
            )}

            {transcriptStatus === "failed" && (
              <div className="flex items-center gap-2">
                <span className={styles.transcriptFailed}>
                  {t("healthMemory.transcript.failed")}
                </span>
                <MButton
                  variant="outline"
                  size="sm"
                  disabled={isRetrying}
                  aria-busy={isRetrying}
                  loading={isRetrying}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setIsRetrying(true);
                    try {
                      await onRetryTranscript?.(id!);
                    } catch {
                      showToast({
                        message: t("healthMemory.transcript.retryFailToast"),
                        variant: "error",
                      });
                    } finally {
                      setIsRetrying(false);
                    }
                  }}
                >
                  {t("healthMemory.transcript.retry")}
                </MButton>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Options Menu */}
      {optionsMenu && optionsMenu.length > 0 && (
        <div className={styles.optionsMenu}>
          <MOptionsMenu
            options={optionsMenu.map((opt) => ({
              ...opt,
              icon: opt.icon || <span className="inline-block w-5" />,
            }))}
          />
        </div>
      )}
    </div>
  );
};

export default HealthMemoryCard;
